import { ContentCluster } from '@app/components/base/content-structure/ContentCluster';
import {
  KpiCardProgress,
  KpiCardValue,
  KpiValue,
  resolveKpiProgressGradientColor,
} from '@app/components/kpi';
import {
  AuditReportsGipAltReportResultDto,
  AuditReportsGipReportResultDto,
} from '@mi-front/api-v2';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { defineMessage, defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { countGipMaterialsStatus } from '../../../../common/count-gip-missing-materials';
import { resolveGipMissingMaterialsGradientColor } from '../../../../common/resolve-gip-missing-materials-gradient-color';

interface GipReportStatsSummaryProps {
  result: AuditReportsGipReportResultDto | AuditReportsGipAltReportResultDto;
}

const summaryTitleMessage = defineMessage({
  id: 'audit.gip.report.summary',
  defaultMessage: 'Resumen',
});

const kpiMessages = defineMessages({
  totalSchedules: {
    id: 'audit.gip.report.totalSchedules',
    defaultMessage: 'Total de schedules',
  },
  withMatch: {
    id: 'audit.gip.report.withMatch',
    defaultMessage: 'Con match',
  },
  withoutMatch: {
    id: 'audit.gip.report.withoutMatch',
    defaultMessage: 'Sin match',
  },
  efficiency: {
    id: 'audit.gip.report.efficiency',
    defaultMessage: 'Eficiencia',
  },
  efficiencyHelp: {
    id: 'audit.gip.report.efficiencyHelp',
    defaultMessage: 'Porcentaje de schedules procesados que tienen un match confirmado.',
  },
  scheduleProgress: {
    id: 'audit.gip.report.scheduleProgress',
    defaultMessage: 'Progreso de schedules',
  },
  scheduleProgressHelp: {
    id: 'audit.gip.report.scheduleProgressHelp',
    defaultMessage:
      'Porcentaje de schedules ya procesados sobre el total. Los pendientes y futuros aún no cuentan como procesados.',
  },
  missingMaterials: {
    id: 'audit.gip.report.missingMaterials',
    defaultMessage: 'Materiales faltantes',
  },
  missingMaterialsHelp: {
    id: 'audit.gip.report.missingMaterialsHelp',
    defaultMessage: 'Cantidad de materiales referenciados que aún no están cargados.',
  },
});

const kpiRowGridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
    lg: 'repeat(6, minmax(0, 1fr))',
  },
} as const;

/**
 * Rounds percentage KPIs for display: one decimal below 1%, otherwise nearest integer.
 * Avoids flooring small non-zero rates to 0%.
 */
function formatPercentMetric(value: number | undefined): number {
  if (value == null || Number.isNaN(value)) {
    return 0;
  }
  if (value > 0 && value < 1) {
    return Math.round(value * 10) / 10;
  }
  return Math.round(value);
}

/**
 * Maps a part/whole ratio to the KPI progress gradient rate in [0, 1].
 * When `invert` is true, a higher share is treated as worse (e.g. unmatched).
 * Empty denominators resolve to a healthy rate so zero-state strips stay green.
 */
function toHealthRate(part: number, whole: number, invert = false): number {
  if (whole <= 0) {
    return 1;
  }
  const share = part / whole;
  return invert ? 1 - share : share;
}

/**
 * Dense GIP summary strip: match coverage, efficiency/progress with help, and missing materials.
 */
export function GipReportStatsSummary({ result }: GipReportStatsSummaryProps) {
  const { $t, locale } = useIntl();
  const theme = useTheme();
  const { meta, matchedSchedules, unmatchedSchedules, audiosStatusByStation } = result;
  const matchedCount = matchedSchedules.length;
  const unmatchedCount = unmatchedSchedules.length;
  const totalSchedules = matchedCount + unmatchedCount;
  const efficiency = formatPercentMetric(meta.matchedEfficiency);
  const progress = formatPercentMetric(meta.processedProgress);
  const { missing: missingMaterials, total: totalMaterials } =
    countGipMaterialsStatus(audiosStatusByStation);

  const matchHealthColor = resolveKpiProgressGradientColor(
    toHealthRate(matchedCount, totalSchedules),
    theme.palette,
  );
  const unmatchedHealthColor = resolveKpiProgressGradientColor(
    toHealthRate(unmatchedCount, totalSchedules, true),
    theme.palette,
  );
  const missingMaterialsRatio = totalMaterials > 0 ? missingMaterials / totalMaterials : 0;
  const materialsHealthColor = resolveGipMissingMaterialsGradientColor(
    missingMaterialsRatio,
    theme.palette,
  );

  return (
    <Box sx={{ mb: 3 }}>
      <ContentCluster title={<FormattedMessage {...summaryTitleMessage} />} divider={false}>
        <Box sx={kpiRowGridSx}>
          <KpiCardProgress
            size="sm"
            title={$t(kpiMessages.efficiency)}
            progress={efficiency}
            help={$t(kpiMessages.efficiencyHelp)}
          />
          <KpiCardProgress
            size="sm"
            title={$t(kpiMessages.scheduleProgress)}
            progress={progress}
            help={$t(kpiMessages.scheduleProgressHelp)}
          />
          <KpiCardValue
            size="sm"
            title={$t(kpiMessages.totalSchedules)}
            value={<KpiValue type="number" locale={locale} value={totalSchedules} />}
          />
          <KpiCardValue
            size="sm"
            title={$t(kpiMessages.withMatch)}
            value={
              <KpiValue
                type="ratio"
                locale={locale}
                numerator={matchedCount}
                denominator={totalSchedules}
                color={matchHealthColor}
              />
            }
          />
          <KpiCardValue
            size="sm"
            title={$t(kpiMessages.withoutMatch)}
            value={
              <KpiValue
                type="ratio"
                locale={locale}
                numerator={unmatchedCount}
                denominator={totalSchedules}
                color={unmatchedHealthColor}
              />
            }
          />
          <KpiCardValue
            size="sm"
            title={$t(kpiMessages.missingMaterials)}
            help={$t(kpiMessages.missingMaterialsHelp)}
            value={
              <KpiValue
                type="ratio"
                locale={locale}
                numerator={missingMaterials}
                denominator={totalMaterials}
                color={materialsHealthColor}
              />
            }
          />
        </Box>
      </ContentCluster>
    </Box>
  );
}
